import { api } from '@/api';
import {
  QaEquipmentCatalogDto,
  QaEquipmentRtSystemDto,
  QaEquipmentSubCategory,
  QaRtSystemTemplateContainerDto,
  TaskTypeDescription,
} from '@/api/generator';

export const loadQaRtSystemTemplateContainers = async (
  taskType: TaskTypeDescription,
  onSuccess: (containers: QaRtSystemTemplateContainerDto[]) => void,
  ids: Array<string> = [],
) => {
  const { result, success } = await api.taskItemsApi.getTaskItemQaRtSystemTemplate({
    getQaRtSystemTemplateDto: {
      taskType,
      supportSystemEntityIds: ids,
    },
  });
  if (success) {
    onSuccess(result.containers || []);
  }
};

// The helpers below take the full RT system DTO (task setup) as well as the
// catalog DTO (job occurrence); both carry the sub category they look at.
export type BeamAnalysisQaSystem = QaEquipmentRtSystemDto | QaEquipmentCatalogDto;

/** Detector interface role in a beam analysis group. */
export const isBeamAnalysisInterface = (system: BeamAnalysisQaSystem | undefined): boolean =>
  system?.subCategory === QaEquipmentSubCategory.Interface;

/** Array detector role in a beam analysis group. */
export const isBeamAnalysisArray = (system: BeamAnalysisQaSystem | undefined): boolean =>
  system?.subCategory === QaEquipmentSubCategory.ArrayDetector;

/**
 * Display order for the equipment of a beam analysis group: the detector
 * interface takes the first slot and the array detector the second, so the
 * two can be shown as a connected pair. Everything else keeps its order.
 * The persisted order of the group is not touched.
 */
export const orderSystemsForBeamAnalysis = <T extends BeamAnalysisQaSystem>(systems: T[]): T[] => {
  const detectorInterface = systems.find(isBeamAnalysisInterface);
  const arrayDetector = systems.find(isBeamAnalysisArray);
  const leading = [detectorInterface, arrayDetector].filter(
    (system): system is T => system !== undefined,
  );
  const rest = systems.filter((system) => !leading.includes(system));
  return [...leading, ...rest];
};

/** True when the first two systems are the interface/array pair of a beam analysis group. */
export const isBeamAnalysisPair = (systems: BeamAnalysisQaSystem[]): boolean =>
  isBeamAnalysisInterface(systems[0]) && isBeamAnalysisArray(systems[1]);
